import React, { useState, useEffect, useRef } from 'react';

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Auto-rotate testimonials
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
    }

    const el = sectionRef.current;
    return () => {
      if (el) observer.unobserve(el);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  const testimonials = [
    {
      id: 1,
      name: "Ahmed Hassan",
      role: "Commercial Farm Owner",
      company: "Nile Delta Agribusiness",
      location: "Cairo, Egypt",
      content: "We installed DuaAxis trackers on our farm and saw a 42% increase in energy production compared to our old fixed panels. The ROI was much faster than we anticipated, and the system has been incredibly reliable in our desert climate.",
      rating: 5,
      image: "/testimonial-ahmed.jpg",
      stats: {
        system: "150kW System",
        production: "45% increase",
        payback: "4.2 years ROI",
        savings: "$18,500/year"
      },
      video: "/testimonials/ahmed-review.mp4",
      logo: "/logos/nile-delta.png"
    },
    {
      id: 2,
      name: "Fatima Mahmoud",
      role: "Manufacturing Plant Director",
      company: "Red Sea Manufacturing",
      location: "Alexandria, Egypt",
      content: "The IoT monitoring system is fantastic. We can track performance in real-time and the system automatically optimizes based on weather conditions. Maintenance has been minimal even in our coastal environment with high salinity.",
      rating: 5,
      image: "/testimonial-fatima.jpg",
      stats: {
        system: "500kW System",
        production: "48% more energy",
        payback: "3.8 years ROI",
        savings: "$62,000/year"
      },
      video: "/testimonials/fatima-review.mp4",
      logo: "/logos/red-sea.png"
    },
    {
      id: 3,
      name: "Mohamed Ali",
      role: "Residential Community Manager",
      company: "Giza Heights Community",
      location: "Giza, Egypt",
      content: "We needed to maximize energy production in a limited space for our community center. DuaAxis was the perfect solution, generating significantly more power than traditional panels would have. Our residents love the sustainability story.",
      rating: 5,
      image: "/testimonial-mohamed.jpg",
      stats: {
        system: "75kW System",
        production: "43% increase",
        payback: "4.5 years ROI",
        savings: "$9,200/year"
      },
      video: "/testimonials/mohamed-review.mp4",
      logo: "/logos/giza-heights.png"
    },
    {
      id: 4,
      name: "Layla Abdel",
      role: "Hotel General Manager",
      company: "Luxor Resort & Spa",
      location: "Luxor, Egypt",
      content: "As a luxury resort, we needed a solar solution that was both efficient and aesthetically pleasing. DuaAxis delivered on both counts. Our energy costs have decreased by 60% while maintaining our property's beautiful appearance.",
      rating: 5,
      image: "/testimonial-layla.jpg",
      stats: {
        system: "200kW System",
        production: "52% more energy",
        payback: "3.5 years ROI",
        savings: "$24,800/year"
      },
      video: "/testimonials/layla-review.mp4",
      logo: "/logos/luxor-resort.png"
    },
    {
      id: 5,
      name: "Omar Sherif",
      role: "Industrial Facility Manager",
      company: "Suez Industrial Park",
      location: "Suez, Egypt",
      content: "The durability of DuaAxis systems in harsh industrial environments impressed us. Despite dust, heat, and occasional sandstorms, the trackers have maintained peak performance with minimal maintenance required.",
      rating: 5,
      image: "/testimonial-omar.jpg",
      stats: {
        system: "1.2MW System",
        production: "47% increase",
        payback: "4.0 years ROI",
        savings: "$148,000/year"
      },
      video: "/testimonials/omar-review.mp4",
      logo: "/logos/suez-park.png"
    }
  ];

  const stats = [
    { value: "98%", label: "Customer Satisfaction", icon: "😊", color: "from-green-500 to-emerald-600" },
    { value: "45%", label: "Average Energy Increase", icon: "⚡", color: "from-yellow-500 to-orange-500" },
    { value: "4.2", label: "Years Average ROI", icon: "💰", color: "from-blue-500 to-cyan-600" },
    { value: "500+", label: "Happy Customers", icon: "👥", color: "from-purple-500 to-pink-600" }
  ];

  const industries = [
    { name: "Agriculture", count: 120, icon: "🌾" },
    { name: "Manufacturing", count: 85, icon: "🏭" },
    { name: "Hospitality", count: 65, icon: "🏨" },
    { name: "Residential", count: 150, icon: "🏠" },
    { name: "Industrial", count: 80, icon: "⚙️" }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section ref={sectionRef} id="testimonials" className="relative py-20 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-primary-50/30 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-100/20 via-transparent to-transparent"></div>
      <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-r from-secondary-200/30 to-primary-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-r from-primary-200/20 to-secondary-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-20">
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-200 text-primary-700 text-sm font-semibold py-3 px-6 rounded-full mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></span>
              Trusted by Egypt's Leading Businesses
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Real <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Results</span>
              <br />
              Real <span className="bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent">Stories</span>
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover why forward-thinking businesses across Egypt are achieving unprecedented energy savings with DuaAxis solar tracking technology
            </p>
          </div>
        </div>

        {/* Interactive Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`group relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl border border-white/50 text-center transform transition-all duration-500 hover:-translate-y-2 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br rounded-2xl opacity-0 group-hover:opacity-5 transition-opacity duration-300 ${stat.color}`}></div>
              <div className="text-4xl mb-3">{stat.icon}</div>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-sm font-medium text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Industry Distribution */}
        <div className={`mb-16 transform transition-all duration-700 delay-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Serving Diverse Industries Across Egypt</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry, index) => (
              <div 
                key={industry.name}
                className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/50 group hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <span className="text-2xl mr-3 group-hover:scale-110 transition-transform duration-300">{industry.icon}</span>
                <span className="font-semibold text-gray-800 mr-2">{industry.name}</span>
                <span className="text-sm bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                  {industry.count}+ projects
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Testimonials Grid with Enhanced Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id}
              className={`group relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl hover:shadow-3xl border border-white/50 overflow-hidden transform transition-all duration-500 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              } ${hoveredCard === testimonial.id ? 'scale-105 -translate-y-4' : 'hover:scale-105 hover:-translate-y-2'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onMouseEnter={() => setHoveredCard(testimonial.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Animated Border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500 to-secondary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-[2px] rounded-3xl bg-white"></div>
              </div>

              <div className="relative p-8">
                {/* Company Logo */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {testimonial.logo ? (
                        <img src={testimonial.logo} alt={testimonial.company} className="w-8 h-8" />
                      ) : (
                        testimonial.name.charAt(0)
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{testimonial.name}</h3>
                      <p className="text-sm text-primary-600 font-medium">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="h-5 w-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a 1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6 italic relative">
                  <span className="absolute -left-2 -top-2 text-4xl text-primary-200 opacity-50">"</span>
                  {testimonial.content}
                  <span className="absolute -right-2 -bottom-2 text-4xl text-primary-200 opacity-50">"</span>
                </p>

                {/* Enhanced Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {Object.entries(testimonial.stats).map(([key, value], idx) => (
                    <div key={key} className="text-center p-3 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="text-sm font-semibold text-primary-600 mb-1">{value}</div>
                      <div className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                    </div>
                  ))}
                </div>

                {/* Location & Video Badge */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {testimonial.location}
                  </div>
                  {testimonial.video && (
                    <button className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium group">
                      <svg className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Watch Story
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Featured Testimonial Carousel */}
        <div className={`relative bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 rounded-3xl p-8 lg:p-12 text-white mb-16 transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
        }`}>
          {/* Play/Pause Controls */}
          <div className="absolute top-6 right-6 flex items-center space-x-2">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-200"
              aria-label={isPlaying ? "Pause rotation" : "Play rotation"}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                {isPlaying ? (
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                )}
              </svg>
            </button>
          </div>

          <div className="flex flex-col lg:flex-row items-center lg:items-start">
            <div className="flex-1 lg:pr-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                  {testimonials[currentTestimonial].logo ? (
                    <img src={testimonials[currentTestimonial].logo} alt={testimonials[currentTestimonial].company} className="w-10 h-10" />
                  ) : (
                    <span className="text-2xl text-white font-bold">
                      {testimonials[currentTestimonial].name.charAt(0)}
                    </span>
                  )}
                </div>
                <div>
                  <div className="flex mb-2">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <svg key={i} className="h-6 w-6 text-yellow-300 fill-current mr-1" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <h3 className="text-2xl font-bold">{testimonials[currentTestimonial].name}</h3>
                  <p className="text-primary-200 text-lg">{testimonials[currentTestimonial].role}</p>
                  <p className="text-primary-300">{testimonials[currentTestimonial].company} • {testimonials[currentTestimonial].location}</p>
                </div>
              </div>
              
              <p className="text-xl lg:text-2xl italic leading-relaxed mb-8 relative">
                <span className="absolute -left-4 -top-4 text-5xl text-primary-300 opacity-50">"</span>
                {testimonials[currentTestimonial].content}
                <span className="absolute -right-4 -bottom-4 text-5xl text-primary-300 opacity-50">"</span>
              </p>

              {/* Performance Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {Object.entries(testimonials[currentTestimonial].stats).map(([key, value]) => (
                  <div key={key} className="text-center p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    <div className="text-lg font-bold text-white">{value}</div>
                    <div className="text-sm text-primary-200 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Controls */}
            <div className="flex lg:flex-col space-x-4 lg:space-x-0 lg:space-y-4 mt-8 lg:mt-0">
              <button
                onClick={prevTestimonial}
                className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 transform hover:scale-110"
                aria-label="Previous testimonial"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonial 
                        ? 'bg-white scale-125' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="p-3 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 transform hover:scale-110"
                aria-label="Next testimonial"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced CTA Section */}
        <div className={`text-center transform transition-all duration-1000 delay-700 ${
          isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
        }`}>
          <div className="bg-gradient-to-r from-white to-gray-50 rounded-3xl p-12 shadow-2xl border border-white/50 backdrop-blur-sm">
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Write <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Your Success Story</span>?
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Join hundreds of satisfied customers who are already enjoying unprecedented energy savings and sustainable power generation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="group relative bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-2xl overflow-hidden">
                <span className="relative z-10">View Detailed Case Studies</span>
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-600 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button className="group relative border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1">
                <span className="relative z-10">Schedule Free Consultation</span>
                <div className="absolute inset-0 bg-primary-600 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full"></div>
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              ⚡ Get a personalized ROI analysis in 24 hours
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;